/**
 * Checks if a value is considered "valid data" for display purposes.
 * Returns false for: null, undefined, empty string, "-", "0 Sq. Ft.", numeric 0, string "0"
 */
export const isValidData = (value) => {
  if (value === null || value === undefined) return false;
  if (value === '' || value === '-' || value === '0 Sq. Ft.') return false;
  if (typeof value === 'number' && value === 0) return false;
  if (value === 0 || value === '0') return false;
  return true;
};

/**
 * Sanitizes bio HTML to only allow safe formatting tags.
 * Strips scripts, event handlers, and disallowed tags while preserving
 * basic formatting (br, p, b, i, strong, em, ul, ol, li, a, span).
 * Used for broker bio and title fields.
 * @param {string|null|undefined} text
 * @returns {string}
 */
export const sanitizeBioText = (text) => {
  if (!text) return '';
  const allowedTags = ['br', 'p', 'b', 'i', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'span'];
  // Remove script tags and event handlers entirely
  let sanitized = text.replace(/<script[\s\S]*?<\/script>/gi, '');
  sanitized = sanitized.replace(/on\w+="[^"]*"/gi, '');
  sanitized = sanitized.replace(/on\w+='[^']*'/gi, '');
  // Remove tags not in allowlist
  sanitized = sanitized.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/gi, (match, tag) => {
    if (allowedTags.includes(tag.toLowerCase())) {
      if (tag.toLowerCase() === 'a') {
        const hrefMatch = match.match(/href=["']([^"']*)["']/i);
        if (hrefMatch) {
          const isClosing = match.startsWith('</');
          return isClosing ? '</a>' : `<a href="${hrefMatch[1]}" target="_blank" rel="noopener noreferrer">`;
        }
        return match.startsWith('</') ? '</a>' : '<a>';
      }
      return match.startsWith('</') ? `</${tag}>` : `<${tag}>`;
    }
    return '';
  });
  return sanitized;
};

/**
 * Builds the standard pipe-delimited event label for Commercial PDP analytics.
 * Format: propertyId|listingId|streetNumber streetName, county, state, city, zip|Referrer: Other|Foreclosure Property: Yes/No|Status: statusValue
 * @param {Object} pdpInitData - The full PDP init data from Redux
 * @returns {string} Formatted event label
 */
export const buildEventLabel = (pdpInitData) => {
  const details = pdpInitData?.propertyDetails;
  const address = details?.address;
  const isForeclosure = details?.commonAttributes?.propCategory === "FORECLOSURE";
  const status = details?.commonAttributes?.status || "N/A";

  const propertyId = details?.propertyId || "";
  const listingId = details?.listingId || "";
  const streetNumber = address?.streetNumber || "";
  const streetName = address?.streetName || "";
  const county = address?.county || "";
  const state = address?.state || "";
  const city = address?.city || "";
  const zip = address?.zip || "";

  return `${propertyId}|${listingId}|${streetNumber} ${streetName}, ${county}, ${state}, ${city}, ${zip}|Referrer: Other|Foreclosure Property: ${isForeclosure ? "Yes" : "No"}|Status: ${status}`;
};

/**
 * Maps US state abbreviations to full state names.
 */
const US_STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia', PR: 'Puerto Rico', VI: 'Virgin Islands', GU: 'Guam',
};

/**
 * Returns the full state name for a given abbreviation.
 * Falls back to the abbreviation itself if not found.
 */
export const getFullStateName = (abbr) => {
  if (!abbr) return '';
  return US_STATE_NAMES[abbr.toUpperCase()] || abbr;
};

/**
 * Formats an API folder key (e.g. "COM_FINANCIALS") into a readable display name.
 * Strips "COM_" prefix, converts UPPER_SNAKE_CASE to Title Case.
 * Uses a lookup map for keys that need special formatting (e.g. ampersands).
 * If the key is already human-readable (no underscore pattern), returns as-is.
 */
const FOLDER_DISPLAY_NAMES = {
  'COM_OFFERING': 'The Offering',
  'COM_THE_OFFERING': 'The Offering',
  'COM_PURCHASE_SALE_AGREEMENT': 'Purchase & Sale Agreement Sample',
  'COM_PURCHASE_AND_SALE_AGREEMENT': 'Purchase & Sale Agreement Sample',
  'COM_TITLE_AND_INSURANCE': 'Title & Insurance',
  'COM_REAL_ESTATE_BILLS': 'Real Estate Bills',
  'COM_THIRD_PARTY_REPORTS': 'Third Party Reports',
  'COM_RENT_ROLL': 'Rent Roll',
  'COM_FINANCIALS': 'Financials',
  'COM_UTILITIES': 'Utilities',
  'COM_MISC': 'Misc',
  'COM_DISCLOSURES': 'Disclosures',
  'COMMERCIAL_OTHER': 'Other',
};

export const formatFolderDisplayName = (key) => {
  if (!key) return '';
  // Check lookup map first for known keys
  if (FOLDER_DISPLAY_NAMES[key]) return FOLDER_DISPLAY_NAMES[key];
  // Strip COM_ prefix if present
  const stripped = key.startsWith('COM_') ? key.slice(4) : key;
  // If it looks like UPPER_SNAKE_CASE, convert to Title Case
  if (/^[A-Z0-9_]+$/.test(stripped)) {
    return stripped
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  // Already readable (e.g. "The Offering")
  return stripped;
};
